
import { useState, useCallback, useRef, useEffect } from 'react';
import { Point, Transform, CanvasSize, screenToCanvas, clampCanvasPosition, snapToPixel } from './coordinateUtils';

export interface CardPosition {
  x: number;
  y: number;
  zIndex: number;
}

export interface DragState {
  isDragging: boolean;
  draggedCardIds: string[];
  dragOffset: Point;
  initialPositions: Record<string, Point>;
}

export interface UseDragPositionProps {
  contentItems: Array<{ id: string }>;
  calculateCardPosition: (index: number) => Point;
  transform: Transform;
  canvasSize: CanvasSize;
  savedPositions: Record<string, { x: number; y: number }>;
  onPositionSaved: (contentId: string, x: number, y: number) => Promise<void>;
  gridSize?: number;
  selectedIds?: string[];
}

export const useDragPosition = ({
  contentItems,
  calculateCardPosition,
  transform,
  canvasSize,
  savedPositions,
  onPositionSaved,
  gridSize = 50,
  selectedIds = []
}: UseDragPositionProps) => {
  // Initialize positions using saved positions or grid layout - UI STATE IS AUTHORITATIVE
  const [cardPositions, setCardPositions] = useState<Record<string, CardPosition>>(() => {
    const positions: Record<string, CardPosition> = {};
    contentItems.forEach((item, index) => {
      const savedPos = savedPositions[item.id];
      const gridPos = calculateCardPosition(index);
      
      // CRITICAL FIX: Ensure positions are snapped to grid on initialization
      let finalPos;
      if (savedPos) {
        finalPos = { x: savedPos.x, y: savedPos.y };
      } else {
        // Snap grid positions to ensure they align with grid intersections
        finalPos = {
          x: Math.round(gridPos.x / gridSize) * gridSize,
          y: Math.round(gridPos.y / gridSize) * gridSize
        };
      }
      
      positions[item.id] = {
        x: finalPos.x,
        y: finalPos.y,
        zIndex: 1
      };
    });
    return positions;
  });

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedCardIds: [],
    dragOffset: { x: 0, y: 0 },
    initialPositions: {}
  });

  const dragStartRef = useRef<Point>({ x: 0, y: 0 });

  // CRITICAL FIX 1: Sync when savedPositions loads from database
  useEffect(() => {
    if (Object.keys(savedPositions).length > 0) {
      console.log(`[DRAG POSITION] Syncing UI with ${Object.keys(savedPositions).length} loaded positions`);
      setCardPositions(prev => {
        const newPositions = { ...prev };
        let hasChanges = false;
        
        // Update existing items with saved positions
        contentItems.forEach(item => {
          const savedPos = savedPositions[item.id];
          if (savedPos && newPositions[item.id]) {
            const currentPos = newPositions[item.id];
            if (Math.abs(currentPos.x - savedPos.x) > 1 || Math.abs(currentPos.y - savedPos.y) > 1) {
              console.log(`[DRAG POSITION] Updating ${item.id} from saved position (${savedPos.x}, ${savedPos.y})`);
              newPositions[item.id] = {
                ...currentPos,
                x: savedPos.x,
                y: savedPos.y
              };
              hasChanges = true;
            }
          }
        });
        
        return hasChanges ? newPositions : prev;
      });
    }
  }, [savedPositions, contentItems]);

  // Only sync new items that don't exist in current state
  useEffect(() => {
    setCardPositions(prev => {
      const newPositions = { ...prev };
      let hasChanges = false;
      
      contentItems.forEach((item, index) => {
        if (!newPositions[item.id]) {
          // Only for NEW items that don't exist in current state
          const savedPos = savedPositions[item.id];
          const gridPos = calculateCardPosition(index);
          
          // CRITICAL FIX: Ensure new items are also snapped to grid
          let finalPos;
          if (savedPos) {
            finalPos = { x: savedPos.x, y: savedPos.y };
          } else {
            finalPos = {
              x: Math.round(gridPos.x / gridSize) * gridSize,
              y: Math.round(gridPos.y / gridSize) * gridSize
            };
          }
          
          newPositions[item.id] = {
            x: finalPos.x,
            y: finalPos.y,
            zIndex: 1
          };
          hasChanges = true;
          console.log(`[DRAG POSITION] Added new item ${item.id} at grid-snapped position (${finalPos.x}, ${finalPos.y})`);
        }
      });
      
      return hasChanges ? newPositions : prev;
    });
  }, [contentItems, calculateCardPosition, gridSize]);

  // Start dragging a card or selection
  const startDrag = useCallback((cardId: string, mouseEvent: React.MouseEvent) => {
    const currentPosition = cardPositions[cardId];
    if (!currentPosition) return;

    let cardsToDrag: string[];
    if (selectedIds.includes(cardId) && selectedIds.length > 1) {
      cardsToDrag = selectedIds.filter(id => cardPositions[id]);
      console.log(`[DRAG POSITION] Starting multi-select drag with ${cardsToDrag.length} cards`);
    } else {
      cardsToDrag = [cardId];
      console.log(`[DRAG POSITION] Starting single card drag for ${cardId}`);
    }

    const mouseCanvas = screenToCanvas(
      { x: mouseEvent.clientX, y: mouseEvent.clientY },
      transform,
      canvasSize
    );

    const dragOffset = {
      x: mouseCanvas.x - currentPosition.x,
      y: mouseCanvas.y - currentPosition.y
    };

    const initialPositions: Record<string, Point> = {};
    cardsToDrag.forEach(id => {
      const pos = cardPositions[id];
      if (pos) {
        initialPositions[id] = { x: pos.x, y: pos.y };
      }
    });

    dragStartRef.current = { x: mouseEvent.clientX, y: mouseEvent.clientY };

    setDragState({
      isDragging: true,
      draggedCardIds: cardsToDrag,
      dragOffset,
      initialPositions
    });

    // Raise z-index for dragged cards
    setCardPositions(prev => {
      const newPositions = { ...prev };
      cardsToDrag.forEach(id => {
        if (newPositions[id]) {
          newPositions[id] = { ...newPositions[id], zIndex: 1000 };
        }
      });
      return newPositions;
    });
  }, [cardPositions, transform, canvasSize, selectedIds]);

  // Update card positions during drag
  const updateDrag = useCallback((mouseEvent: React.MouseEvent) => {
    if (!dragState.isDragging || dragState.draggedCardIds.length === 0) return;

    const mouseCanvas = screenToCanvas(
      { x: mouseEvent.clientX, y: mouseEvent.clientY },
      transform,
      canvasSize
    );

    const primaryCardId = dragState.draggedCardIds[0];
    const primaryNewPosition = {
      x: mouseCanvas.x - dragState.dragOffset.x,
      y: mouseCanvas.y - dragState.dragOffset.y
    };

    const primaryInitialPos = dragState.initialPositions[primaryCardId];
    if (!primaryInitialPos) return;

    const deltaX = primaryNewPosition.x - primaryInitialPos.x;
    const deltaY = primaryNewPosition.y - primaryInitialPos.y;

    setCardPositions(prev => {
      const newPositions = { ...prev };
      
      dragState.draggedCardIds.forEach(cardId => {
        const initialPos = dragState.initialPositions[cardId];
        if (initialPos && newPositions[cardId]) {
          const newPosition = {
            x: initialPos.x + deltaX,
            y: initialPos.y + deltaY
          };

          const clampedPosition = clampCanvasPosition(newPosition);
          const snappedPosition = snapToPixel(clampedPosition);

          newPositions[cardId] = {
            ...newPositions[cardId],
            x: snappedPosition.x,
            y: snappedPosition.y
          };
        }
      });
      
      return newPositions;
    });
  }, [dragState, transform, canvasSize]);

  // CRITICAL FIX 2: Fixed endDrag to properly capture positions before saving
  const endDrag = useCallback(async () => {
    if (!dragState.isDragging || dragState.draggedCardIds.length === 0) return;

    console.log(`[DRAG POSITION] Ending drag for ${dragState.draggedCardIds.length} cards`);

    // Capture current positions and handle saving synchronously
    setCardPositions(prev => {
      const newPositions = { ...prev };
      const positionChanges: Array<{ id: string; x: number; y: number }> = [];
      
      // Reset z-index and capture final positions
      dragState.draggedCardIds.forEach(cardId => {
        const initialPos = dragState.initialPositions[cardId];
        const currentPos = newPositions[cardId];
        
        if (newPositions[cardId]) {
          newPositions[cardId] = { ...newPositions[cardId], zIndex: 1 };
        }
        
        if (initialPos && currentPos && 
            (Math.abs(currentPos.x - initialPos.x) > 1 || Math.abs(currentPos.y - initialPos.y) > 1)) {
          positionChanges.push({
            id: cardId,
            x: currentPos.x,
            y: currentPos.y
          });
          console.log(`[DRAG POSITION] Position changed for ${cardId}: (${initialPos.x}, ${initialPos.y}) -> (${currentPos.x}, ${currentPos.y})`);
        }
      });

      // Save all position changes immediately
      if (positionChanges.length > 0) {
        console.log(`[DRAG POSITION] Immediately saving ${positionChanges.length} position changes`);
        Promise.all(
          positionChanges.map(change => {
            console.log(`[DRAG POSITION] Saving ${change.id} at (${change.x}, ${change.y})`);
            return onPositionSaved(change.id, change.x, change.y);
          })
        ).then(() => {
          console.log(`[DRAG POSITION] Successfully saved all ${positionChanges.length} positions`);
        }).catch(error => {
          console.error('[DRAG POSITION] Error saving positions:', error);
        });
      } else {
        console.log('[DRAG POSITION] No position changes detected, no saves needed');
      }
      
      return newPositions;
    });

    // Clear drag state
    setDragState({
      isDragging: false,
      draggedCardIds: [],
      dragOffset: { x: 0, y: 0 },
      initialPositions: {}
    });
  }, [dragState, onPositionSaved]);

  // Reset all positions to grid layout
  const resetPositionsToGrid = useCallback(() => {
    console.log("[DRAG POSITION] Resetting all positions to grid layout");
    
    setCardPositions(prev => {
      const newPositions: Record<string, CardPosition> = {};
      
      contentItems.forEach((item, index) => {
        const gridPos = calculateCardPosition(index);
        // CRITICAL FIX: Ensure reset positions are also snapped to grid
        const snappedPos = {
          x: Math.round(gridPos.x / gridSize) * gridSize,
          y: Math.round(gridPos.y / gridSize) * gridSize
        };
        
        newPositions[item.id] = {
          x: snappedPos.x,
          y: snappedPos.y,
          zIndex: 1
        };
      });
      
      return newPositions;
    });
  }, [contentItems, calculateCardPosition, gridSize]);

  // CRITICAL FIX 3: Fixed alignPositionsToGrid to properly snap to grid intersections
  const alignPositionsToGrid = useCallback(async () => {
    console.log("[DRAG POSITION] Aligning all positions to grid intersections");
    
    setCardPositions(prev => {
      const newPositions = { ...prev };
      const positionChanges: Array<{ id: string; x: number; y: number }> = [];
      
      contentItems.forEach((item) => {
        const currentPos = prev[item.id];
        if (currentPos) {
          // CRITICAL FIX: Proper grid alignment calculation
          // Snap to nearest grid intersection (upper-left corner alignment)
          const snappedX = Math.round(currentPos.x / gridSize) * gridSize;
          const snappedY = Math.round(currentPos.y / gridSize) * gridSize;
          const clampedPosition = clampCanvasPosition({ x: snappedX, y: snappedY });
          
          if (Math.abs(currentPos.x - clampedPosition.x) > 1 || Math.abs(currentPos.y - clampedPosition.y) > 1) {
            newPositions[item.id] = {
              ...currentPos,
              x: clampedPosition.x,
              y: clampedPosition.y
            };
            
            positionChanges.push({
              id: item.id,
              x: clampedPosition.x,
              y: clampedPosition.y
            });
            console.log(`[DRAG POSITION] Aligning ${item.id}: (${currentPos.x}, ${currentPos.y}) -> (${clampedPosition.x}, ${clampedPosition.y})`);
          }
        }
      });
      
      // Save all aligned positions immediately
      if (positionChanges.length > 0) {
        console.log(`[DRAG POSITION] Immediately saving ${positionChanges.length} aligned positions`);
        Promise.all(
          positionChanges.map(change => {
            console.log(`[DRAG POSITION] Saving aligned ${change.id} at (${change.x}, ${change.y})`);
            return onPositionSaved(change.id, change.x, change.y);
          })
        ).then(() => {
          console.log(`[DRAG POSITION] Successfully saved all aligned positions`);
        }).catch(error => {
          console.error('[DRAG POSITION] Error saving aligned positions:', error);
        });
      } else {
        console.log('[DRAG POSITION] No alignment changes needed, no saves required');
      }
      
      return newPositions;
    });
  }, [contentItems, gridSize, onPositionSaved]);

  return {
    cardPositions,
    dragState,
    startDrag,
    updateDrag,
    endDrag,
    resetPositionsToGrid,
    alignPositionsToGrid
  };
};
